<template>
  <!--
    **記事が0件のセクションは丸ごと出さない。** 引けなかった（note が落ちている・
    フィードが空）ときに見出しと「すべて見る」だけが残ると、中身の無い枠が
    トップに居座る。0件なら何も出さないほうが破綻しない（2026-08-13 の判断）。
  -->
  <section v-for="section in visibleSections" :key="section.id" class="sec_home-column">
    <PartsHomeTitle :title="section.title" :subtitle="section.subtitle" />

    <div class="column-list">
      <PartsChromeLink
        v-for="article in section.articles"
        :key="article.link"
        :to="article.link"
        class="column-item"
      >
        <div v-if="article.image" class="column-img">
          <img :src="article.image" :alt="article.title" />
        </div>
        <div class="column-body">
          <p class="column-text">{{ article.title }}</p>
          <!-- 日付は JST の `YYYY.MM.DD` でサーバーから来る（TZ で揺れないようにするため） -->
          <p v-if="article.publishedAt" class="column-date">{{ article.publishedAt }}</p>
        </div>
      </PartsChromeLink>
    </div>

    <PartsBtnRound :to="section.indexUrl" :label="section.buttonLabel" />
  </section>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `sec_home-column`（お役立ちコラム）。
 * 専用ページは作らず外部の Note へ送る（決定#29）。
 *
 * セクションは `COLUMN_FEEDS` の数だけ並ぶ。**今回のリニューアルは「お役立ちコラム」1本だけ**で、
 * 「研究・エビデンス」はセクションを分けずに出さない（2026-08-12 の事業側回答）。
 * `v-for` はそのまま残してあるので、出すことになれば `COLUMN_FEEDS` に1件足すだけで復活する。
 *
 * カードは最新 `COLUMN_ARTICLE_LIMIT` 件（**2件**。Figma の横並び2件の形に合わせる）。
 * **絞り込みはサーバー側**で行う（`server/api/note-column.get.ts`）— 全件返すと
 * 使わないぶんが SSR ペイロードに載るため。
 * 「すべて見る」の飛び先はフィードの `channel > link`。**アカウント単位のフィード**なので
 * `https://note.com/orientalbio`（アカウントのトップ）になる。
 * **引けなかったフィードはセクションごと出さない**（`visibleSections`）。
 * 中身の無い見出しと「すべて見る」だけが残るのを避ける
 * （お知らせが0件のときと同じ扱い → `organisms/index/news.vue`）。
 */
import type { ColumnType } from '@/types/domains/index';
import { COLUMN_FEEDS, COLUMN_URL } from '@/consts/ColumnConst';

/** 1セクション分 */
type ColumnSection = ColumnType.FeedSource & {
  indexUrl: string;
  articles: ColumnType.Article[];
  /** 取得中か。0件と区別して「まだ出さない」を判断する */
  isPending: boolean;
};

const { fetchColumnFeed } = useColumn();

// フィードごとに並列で取る（`useAsyncData` の呼び出し自体は setup の同期フェーズで走る）。
// 日付は JST の `YYYY.MM.DD` でサーバーから来るので、ここでもテンプレートでも整形しない。
/**
 * フィードごとの取得状態。**`useAsyncData` の呼び出しは setup の同期フェーズで走る**
 * ので、`.map()` の中でも問題ない（`await` を挟むと SSR で 500 になるので挟まない）。
 */
const feedStates = COLUMN_FEEDS.map((source: ColumnType.FeedSource) => ({
  source,
  state: fetchColumnFeed(source.id),
}));

/**
 * 1セクション分の表示値。**クライアントで後から埋まる**ので `computed` にする
 * （プレーン配列だと取得完了後に更新されない）。
 */
const sections = computed<ColumnSection[]>(() =>
  feedStates.map(({ source, state }) => ({
    ...source,
    // 「すべて見る」の飛び先。フィードの発行元ページが取れればそれを使う
    indexUrl: state.feed.value.indexUrl || COLUMN_URL,
    // 件数はサーバー側で切って返る（`server/api/note-column.get.ts`）
    articles: state.feed.value.articles,
    isPending: state.status.value === 'pending' || state.status.value === 'idle',
  }))
);

/**
 * 出すセクションだけ。**「読み込み中」と「取れたけど0件」を区別する。**
 *
 * - 読み込み中 … まだ何も分からないので出さない（後から下に生える）
 * - 0件（note が落ちている・フィードが空）… 丸ごと出さない。見出しと「すべて見る」だけが
 *   残ると中身の無い枠がトップに居座るため（お知らせが0件のときと同じ扱い）
 *
 * **枠の先取り（プレースホルダ）は置かない。** コラムはトップの**最下部**にあるので、
 * 後から生えても上のコンテンツは動かない。動くのはフッターとサポート導線の位置だけ。
 */
const visibleSections = computed<ColumnSection[]>(() =>
  sections.value.filter((section) => !section.isPending && section.articles.length > 0)
);
</script>
